/**
 * SMS Service
 *
 * Every outgoing SMS in the platform goes through this module, and this module
 * only ever talks to /api/send-sms. It never talks to a provider directly and
 * never holds a credential — the Cloudflare Function picks the provider from
 * the SMS_PROVIDER env var (mock by default), so switching provider is an env
 * change with no app code touched.
 *
 * Two rules carried over from the PPSR work on 2026-08-17:
 *   1. Never trust res.ok alone — check the parsed body.
 *   2. Record the attempt either way. A failed send writes a 'failed' row to
 *      sms_log with the error, so a failure can never look like a success that
 *      simply left no trace.
 *
 * The audit write is best-effort: if sms_log is unavailable the send still
 * reports its real outcome. Logging must never be the reason an SMS fails.
 */

import { supabase } from '../lib/supabase'
import { isOptedOut, recordOptOut } from './smsOptOutService'
import { apiHeadersOrThrow } from '../lib/authHeader'

export interface SMSResult {
  success: boolean
  message: string
  /** True when nothing was sent because this had already been sent for this deal.
      Not a failure - the system did the right thing - so screens should say what
      happened rather than showing a red error. */
  alreadySent?: boolean
  /** When the earlier one went, so a person can decide whether to repeat it. */
  alreadySentAt?: string | null
  /** True when the failure will repeat forever — a bad number, an opt-out. */
  permanent?: boolean
  /** True when the recipient has asked not to be contacted. */
  optedOut?: boolean
  /** Which adapter handled it — 'mock' until a real provider is configured. */
  provider?: string | null
  /**
   * True when nothing actually left the building.
   *
   * SMS_PROVIDER defaults to mock, which returns a clean success. Callers that
   * gate on `success` alone would record a send that never happened — the AR
   * ladder would stamp its guard column and never chase the buyer again. Check
   * this before believing a message reached anyone.
   */
  simulated?: boolean
  /**
   * Whatever the provider actually returned, verbatim.
   *
   * Tall Bob does not publish its success response, so the only honest way to
   * learn the shape is to look at a real one. The Admin messaging panel shows
   * this; nothing else reads it.
   */
  raw?: unknown
  details?: {
    phone?: string
    message?: string
    sms_id?: string
  }
}

export interface SendSMSOptions {
  to: string
  message: string
  trigger: string
  dealId?: string | null
  invoiceId?: string | null
  /* Set when somebody is deliberately sending this a second time. The server
     refuses a repeat without one. */
  repeatReason?: string | null
}

/**
 * Where links in SMS point.
 *
 * This was hard-coded to production, so an SMS sent from staging pointed a
 * seller at a different environment. VITE_APP_URL is set per Cloudflare Pages
 * project; fall back to wherever the app is actually running.
 */
const appBase = (): string => {
  const configured = import.meta.env.VITE_APP_URL as string | undefined
  if (configured) return configured.replace(/\/+$/, '')
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return 'https://app.directauto.net.au'
}

/**
 * Where a seller's link points.
 *
 * This used to be `${appBase()}/sign-bos` with the deal id on the end — two
 * problems. The path was not a route at all, so the seller landed on the login
 * screen; and the deal id is an internal identifier that appears in staff URLs
 * and exports, so anyone who had ever seen one could have signed in the
 * seller's name. It is now the seller portal, keyed on deals.seller_token
 * (migration 025), which exists for this and can be rotated to revoke a link.
 */
const SELLER_PORTAL_BASE = `${appBase()}/seller`

/**
 * Australian mobile/landline to E.164. Returns null when the input cannot be
 * read as a usable AU number, so callers can fail loudly instead of sending
 * into the void. (The provider adapters normalise again server-side; doing it
 * here means the number we log is the number we sent to.)
 */
export function formatPhoneNumber(input?: string | null): string | null {
  if (!input) return null
  const digits = input.replace(/\D/g, '')
  if (!digits) return null

  // Every Australian number is nine digits once the leading zero is gone.
  // The previous version checked the prefix but not the length, so "0412 345"
  // came back as "+61412345" and was sent — a truncated number the carrier
  // rejects, or worse, someone else's.
  if (digits.startsWith('61') && digits.length === 11) return `+${digits}`
  if (digits.startsWith('0') && digits.length === 10) return `+61${digits.slice(1)}`
  if (digits.length === 9 && !digits.startsWith('0')) return `+61${digits}`
  return null
}

async function writeSmsLog(row: {
  deal_id?: string | null
  invoice_id?: string | null
  to_number: string
  message: string
  trigger_name: string
  status: 'sent' | 'failed' | 'simulated'
  provider?: string | null
  provider_message_id?: string | null
  provider_sms_id?: string | null
  sender?: string | null
  reference?: string | null
  segments?: number | null
  price?: number | null
  error_detail?: string | null
}): Promise<void> {
  try {
    const { error } = await supabase.from('sms_log').insert(row)
    if (error) console.error('[SMS] sms_log write failed:', error.message)
  } catch (err) {
    console.error('[SMS] sms_log write threw:', err)
  }
}

/**
 * The single send path. Normalises, posts, logs, returns.
 */
export async function sendSMS(opts: SendSMSOptions): Promise<SMSResult> {
  const { to, message, trigger, dealId = null, invoiceId = null } = opts

  const phone = formatPhoneNumber(to)
  if (!phone) {
    return { success: false, message: 'Invalid phone number format' }
  }
  if (!message || !message.trim()) {
    return { success: false, message: 'Refusing to send an empty message' }
  }

  // Asked not to be texted, so we do not text them. Checked before the request
  // rather than after the rejection, so the refusal costs nothing and reads as
  // a decision rather than as a failure.
  if (await isOptedOut(phone)) {
    await writeSmsLog({
      deal_id: dealId,
      invoice_id: invoiceId,
      to_number: phone,
      message,
      trigger_name: trigger,
      status: 'failed',
      error_detail: 'Recipient has opted out of SMS',
    })
    return { success: false, message: `${phone} has opted out of SMS`, optedOut: true, permanent: true }
  }

  let res: Response
  try {
    res = await fetch('/api/send-sms', {
      method: 'POST',
      // Signed-in caller only. The endpoint used to accept anyone, which meant
      // anybody could send from the DirectAuto sender ID at our cost.
      headers: await apiHeadersOrThrow(),
      // The reference is echoed back on the provider's delivery webhooks, so
      // it is how a receipt gets matched to this send later. It is also what
      // shows up in the provider's own reporting, which is where someone looks
      // when a seller says they never got the text.
      /* dealId goes over the wire on its own, as well as inside the reference string.
         The reference is a composite the provider echoes back on delivery receipts;
         the repeat guard on the other end needs a plain deal id, and quietly got
         nothing when all it was given was "TRIGGER-uuid". A guard that silently
         protects nothing is worse than no guard, because everyone believes in it. */
      body: JSON.stringify({
        to: phone,
        message,
        trigger,
        dealId: dealId || null,
        repeatReason: opts.repeatReason || null,
        reference: `${trigger}-${dealId || invoiceId || 'na'}`,
        /* This function writes the sms_log row itself, further down, with the
           segments and price the provider reports back. clientLogs tells the
           endpoint not to write a second one. Any other caller omits it and gets
           an audit row written server-side instead of nothing at all. */
        clientLogs: true,
      }),
    })
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'network error'
    await writeSmsLog({
      deal_id: dealId,
      invoice_id: invoiceId,
      to_number: phone,
      message,
      trigger_name: trigger,
      status: 'failed',
      error_detail: `Request failed: ${detail}`,
    })
    return { success: false, message: `SMS send failed: ${detail}` }
  }

  let body: any = null
  try {
    body = await res.json()
  } catch {
    body = null
  }

  // A send is confirmed by the endpoint saying so, not by it happening to
  // return an id — some providers do not give one. `messageId` is still
  // accepted on its own so an older deployment of the Function keeps working.
  const sent = body ? body.sent === true || Boolean(body.messageId) : false

  /* The repeat guard refused this. It is not a failure and must not be dressed as one:
     nothing went wrong, the system declined to text a seller the same link twice. Saying
     "SMS send failed" here would send somebody looking for a fault that does not exist. */
  if (res.status === 409 && body && (body.code === 'already_done' || body.code === 'just_done')) {
  /* Deliberately no sms_log row here: nothing was sent, and that table records sends.
     The original send is already logged - it is what the guard read in order to refuse
     this one. */
    return {
      success: false,
      alreadySent: true,
      alreadySentAt: body.previousAt || null,
      message: String(body.error || 'Already sent for this deal, so it was not sent again.'),
    }
  }

  if (!res.ok || !sent) {
    const detail =
      (body && (body.error || body.message)) || `HTTP ${res.status} from /api/send-sms`
    await writeSmsLog({
      deal_id: dealId,
      invoice_id: invoiceId,
      to_number: phone,
      message,
      trigger_name: trigger,
      status: 'failed',
      error_detail: detail,
    })
    // The provider telling us the recipient opted out is the only notice we
    // get — they opted out somewhere we never saw. Record it so the ladder
    // stops rather than failing against the same number every day.
    if (body?.optedOut) await recordOptOut(phone, 'provider', detail)

    return {
      success: false,
      message: `SMS send failed: ${detail}`,
      provider: body?.provider ?? null,
      permanent: body?.permanent === true,
      optedOut: body?.optedOut === true,
      raw: body ?? null,
    }
  }

  // The mock provider returns a clean success without sending anything. That
  // must not be logged as 'sent': the AR ladder reads these rows to decide
  // whether a buyer has been chased, and a row saying we texted someone we
  // never texted is worse than no row at all.
  const simulated = body.simulated === true || body.provider === 'mock'

  await writeSmsLog({
    deal_id: dealId,
    invoice_id: invoiceId,
    to_number: phone,
    message,
    trigger_name: trigger,
    status: simulated ? 'simulated' : 'sent',
    error_detail: simulated
      ? 'SMS_PROVIDER is not set on this environment — nothing was sent to the carrier.'
      : null,
    provider: body.provider || null,
    provider_message_id: body.messageId || null,
    provider_sms_id: body.raw?.sms_id || body.messageId || null,
    sender: body.sender || null,
    reference: `${trigger}-${dealId || invoiceId || 'na'}`,
    segments: body.raw?.num_segments ?? null,
    price: body.raw?.price ?? null,
  })

  return {
    success: true,
    simulated,
    message: simulated
      ? `Not sent — this environment has no SMS provider configured, so the message to ${phone} was only simulated.`
      : `SMS sent to ${phone}`,
    provider: body.provider || null,
    raw: body.raw ?? null,
    details: { phone, message, sms_id: body.messageId || undefined },
  }
}

function buildBOSMessage(sellerName: string, sellerToken: string): string {
  return `Hi ${sellerName}, your Vehicle Purchase Agreement is ready to sign. Link: ${SELLER_PORTAL_BASE}/${sellerToken} (expires in 48 hours)`
}

function buildInspectionMessage(sellerName: string, whenText: string): string {
  // Two corrections live in this one line.
  //
  // It used to end "Reply to this number if you need to change it", which was
  // not true: an alphanumeric sender ID cannot receive a reply, so those
  // messages went nowhere. Rather than invent a phone number, it points at the
  // person the seller has already been dealing with.
  //
  // It also used to say the inspection "is booked for" a time. It is not — at
  // the moment this text goes out the inspector has been asked and has not yet
  // answered. Telling a seller to be home on Thursday for an appointment
  // nobody has agreed to is how you waste someone's morning.
  return `Hi ${sellerName}, we have asked our inspector to look at your vehicle on ${whenText} and will confirm once they come back to us. If that time does not suit, let your Direct Auto contact know.`
}

export const smsService = {
  /**
   * Vehicle Purchase Agreement SMS to the seller, with the 14-day signing link.
   * Signature unchanged from the previous mocked implementation — the
   * BOS_SMS_SENT workflow trigger calls this and expects an SMSResult back.
   */
  sendBOS_SMS: async (
    phone: string,
    sellerName: string,
    dealId: string,
    sellerToken: string,
    repeatReason?: string,
  ): Promise<SMSResult> => {
    // No token means no working link. Sending the text anyway would put a
    // broken URL in front of a seller, which is what this whole change exists
    // to stop, so it fails loudly instead.
    if (!sellerToken) {
      return {
        success: false,
        message: 'This deal has no seller link token yet — run migration 025. Nothing was sent.',
      }
    }
    return sendSMS({
      to: phone,
      message: buildBOSMessage(sellerName, sellerToken),
      trigger: 'BOS_SMS_SENT',
      dealId,
      repeatReason,
    })
  },

  /**
   * Inspection confirmation to the seller.
   */
  sendInspectionSMS: async (
    phone: string,
    sellerName: string,
    dealId: string,
    whenText: string
  ): Promise<SMSResult> => {
    return sendSMS({
      to: phone,
      message: buildInspectionMessage(sellerName, whenText),
      trigger: 'INSPECTION_SMS_SENT',
      dealId,
    })
  },

  /**
   * Generic escape hatch for callers that build their own copy (the AR
   * cadence uses this). Still logged and still audited like everything else.
   */
  /**
   * Text the buyer a link to their invoice (Winnie's review, AR Reminders).
   *
   * Invoices could only ever be emailed, and there was no buyer-facing invoice
   * URL to link to at all — the link uses the per-invoice token added in
   * migration 022, which is unguessable and can be rotated to revoke access.
   */
  sendInvoiceLinkSMS: async (
    mobile: string,
    buyerName: string,
    invoiceNumber: string,
    amount: number,
    dueDate: string,
    publicToken: string,
    invoiceId: string,
    dealId?: string | null
  ): Promise<SMSResult> => {
    const due = new Date(dueDate).toLocaleDateString('en-AU')
    const total = `$${Number(amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
    return sendSMS({
      to: mobile,
      message:
        `Hi ${buyerName || 'there'}, invoice ${invoiceNumber} from Direct Auto for ${total} is due ${due}. ` +
        `View and pay: ${appBase()}/invoice/${publicToken}`,
      trigger: 'AR_INVOICE_LINK_SMS',
      invoiceId,
      dealId: dealId ?? null,
    })
  },

  send: sendSMS,
}

export default smsService
